import React, { useState, useEffect, useCallback } from 'react';
import { pqrService, handleApiError } from '../services/api';
import toast from 'react-hot-toast';
import PQRCreateModal from '../components/PQRCreateModal';
import PQRDetailModal from '../components/PQRDetailModal';

const CATEGORY_ICONS = { producto: '🔴', logistica: '🟠', facturacion: '🟡', atencion: '🔵', otros: '⚪' };
const STATUS_COLORS = {
  nuevo: 'bg-blue-100 text-blue-800',
  en_progreso: 'bg-yellow-100 text-yellow-800',
  esperando_cliente: 'bg-purple-100 text-purple-800',
  resuelto: 'bg-green-100 text-green-800',
  escalado: 'bg-red-100 text-red-800',
  cerrado: 'bg-gray-200 text-gray-600',
};
const PRIORITY_COLORS = {
  baja: 'text-green-600', media: 'text-yellow-600', alta: 'text-orange-600', critica: 'text-red-600 font-bold',
};

export default function PQRDashboardPage() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ status: '', category: '', priority: '', search: '' });
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await pqrService.getStats();
      if (res?.data) setStats(res.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  const loadTickets = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: 20, sort_by: 'created_at', sort_dir: 'DESC' };
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      if (filters.search) params.search = filters.search;

      const res = await pqrService.list(params);
      if (res?.data) setTickets(res.data);
      if (res?.pagination) setPagination(res.pagination);
    } catch (err) {
      toast.error(handleApiError(err, 'Error cargando PQR'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadStats(); loadTickets(); }, [loadStats, loadTickets]);

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
  };

  const handleRefresh = () => { loadStats(); loadTickets(); };

  const kpis = stats?.kpis || {};

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📋 PQR — Peticiones, Quejas y Reclamos</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión centralizada de novedades y reclamos</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 shadow-sm">
          + Nuevo PQR
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
          <p className="text-2xl font-bold text-gray-900">{kpis.total || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Total PQR</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-500">
          <p className="text-2xl font-bold text-orange-600">{kpis.abiertos || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Abiertos</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
          <p className="text-2xl font-bold text-green-600">{kpis.cerrados || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Cerrados</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
          <p className="text-2xl font-bold text-red-600">{kpis.criticos || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Críticos</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
          <p className="text-2xl font-bold text-purple-600">
            {kpis.avg_resolution_hours ? `${kpis.avg_resolution_hours}h` : '-'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Tiempo Promedio</p>
        </div>
      </div>

      {/* Distribution Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* By Category */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Por Categoría</h3>
            <div className="space-y-2">
              {(stats.byCategory || []).map(c => {
                const pct = kpis.total ? Math.round((c.count / kpis.total) * 100) : 0;
                return (
                  <div key={c.category} className="flex items-center gap-2">
                    <span className="text-sm w-24 capitalize">{CATEGORY_ICONS[c.category]} {c.category}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div className="bg-blue-400 h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-12 text-right">{c.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Status */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Por Estado</h3>
            <div className="flex flex-wrap gap-2">
              {(stats.byStatus || []).map(s => (
                <div key={s.status} className={`px-3 py-2 rounded-lg text-sm font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>
                  {s.status?.replace(/_/g, ' ')} <span className="font-bold">({s.count})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input type="text" placeholder="🔍 Buscar..." value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm w-48" />
          <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm">
            <option value="">Todos los estados</option>
            <option value="nuevo">Nuevo</option>
            <option value="en_progreso">En Progreso</option>
            <option value="esperando_cliente">Esperando Cliente</option>
            <option value="resuelto">Resuelto</option>
            <option value="escalado">Escalado</option>
            <option value="cerrado">Cerrado</option>
          </select>
          <select value={filters.category} onChange={e => handleFilterChange('category', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm">
            <option value="">Todas las categorías</option>
            <option value="producto">🔴 Producto</option>
            <option value="logistica">🟠 Logística</option>
            <option value="facturacion">🟡 Facturación</option>
            <option value="atencion">🔵 Atención</option>
          </select>
          <select value={filters.priority} onChange={e => handleFilterChange('priority', e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm">
            <option value="">Todas las prioridades</option>
            <option value="critica">🔴 Crítica</option>
            <option value="alta">🟠 Alta</option>
            <option value="media">🟡 Media</option>
            <option value="baja">🟢 Baja</option>
          </select>
          <button onClick={handleRefresh} className="ml-auto px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No hay PQR con estos filtros</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Pedido</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Categoría</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Subtipo</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Estado</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Prioridad</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.map(t => (
                <tr key={t.id} onClick={() => setSelectedId(t.id)}
                  className="hover:bg-blue-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-mono text-gray-600">#{t.id}</td>
                  <td className="px-4 py-3 text-blue-600 font-medium">{t.order_number || '-'}</td>
                  <td className="px-4 py-3">{t.customer_name || '-'}</td>
                  <td className="px-4 py-3 capitalize">{CATEGORY_ICONS[t.category]} {t.category}</td>
                  <td className="px-4 py-3 text-gray-500">{t.subcategory?.replace(/_/g, ' ') || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || ''}`}>
                      {t.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className={`px-4 py-3 capitalize ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(t.created_at).toLocaleDateString('es-CO')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-xs text-gray-500">
              {pagination.total} resultados — Página {pagination.page} de {pagination.totalPages}
            </span>
            <div className="flex gap-2">
              <button disabled={pagination.page <= 1} onClick={() => loadTickets(pagination.page - 1)}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50">← Anterior</button>
              <button disabled={pagination.page >= pagination.totalPages} onClick={() => loadTickets(pagination.page + 1)}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50">Siguiente →</button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <PQRCreateModal isOpen={showCreate} onClose={() => setShowCreate(false)} onCreated={handleRefresh} />
      <PQRDetailModal isOpen={!!selectedId} ticketId={selectedId} onClose={() => setSelectedId(null)} onUpdated={handleRefresh} />
    </div>
  );
}
